import {
  ref,
  onMounted,
  defineComponent,
  computed,
} from "@vue/composition-api";

import LoadingAnimation from "@/components/LoadingAnimation/LoadingAnimation.vue";
import ListPageHeader from "@/components/ListPageHeader/ListPageHeader.vue";

import { LineItem } from "@/store/modules/lineItems";

import { JSConn } from "@/utilities/js-conn";

import { itemRouteBuilder, NamedRts } from "@/router";
import { prettifyLineItemCategory } from "@/models/LineItem";

import { genericSort, sortIcon } from "@/components/helpers";

import { debounce } from "@/utilities/debounce";
import {
  arrowUpHandler,
  arrowDownHandler,
  filterOnInputHandler,
  filterItems,
} from "@/utilities/pickListHelpers";

export default defineComponent({
  name: "lineItems",
  props: {
    useMode: {
      type: String,
      required: false,
    },
  },
  components: {
    LoadingAnimation,
    ListPageHeader,
  },

  setup(props, ctx) {
    const $store = ctx.root.$store;

    const dataLoaded = ref(false);
    const loadingMessage = ref("Loading Line item...");

    const firstInput = ref<null | HTMLInputElement>(null);

    const showMenu = ref(false);

    const filterableKeys: Array<keyof LineItem> = [
      "description",
      "default_rate",
      "category",
    ];

    const filter = ref("");

    const clearFilter = () => {
      filterOnInputHandler(
        apiLineItems,
        lineItems,

        "",
        filter,
        debouncedFilteredLineItems,

        sortKey,
        sortAsc,
        updateSort,

        selectedIndex,
      );
    };

    const filterLineItems = (filter: string, ln: LineItem[]) => {
      return filterItems(filter, filterableKeys, ln);
    };

    const debouncedFilteredLineItems = debounce(filterLineItems, 500);

    // info for sort
    let sortKey = ref<keyof LineItem>("description");
    let sortAsc = ref(true);

    const apiLineItems = computed(() => {
      return $store.getters.lineItems as LineItem[];
    });

    const apiTruckLoadLineItems = computed(() => {
      return $store.getters.truckLoadLineItemsForTable as LineItem[];
    });

    const editRouteBuilder = (id: string) => {
      return itemRouteBuilder(NamedRts.lineItemEditForm, id);
    };

    const lineItems = ref<LineItem[]>([]);

    const localSortIcon = (lSortKey: keyof LineItem) => {
      return sortIcon(sortAsc, sortKey, lSortKey);
    };

    const updateSort = (lSortKey: keyof LineItem) => {
      lineItems.value = genericSort(
        [...lineItems.value],
        sortAsc,
        sortKey,
        lSortKey,
      );
    };

    onMounted(() => {
      const jsConn = new JSConn();
      $store.dispatch("needLineItems", jsConn).then(() => {
        if (props.useMode === "pickerMode") {
          lineItems.value = [...apiTruckLoadLineItems.value];
        } else {
          lineItems.value = [...apiLineItems.value];
        }
        // flip the sort, otherwise on load it would be off
        sortAsc.value = !sortAsc.value;
        updateSort(sortKey.value);
        dataLoaded.value = true;

        if (props.useMode !== "pickerMode") {
          ctx.root.$nextTick(() => {
            if (firstInput.value) {
              firstInput.value.focus();
            }
          });
        }
      });
    });

    // this is for when the line items are used as a picker
    // they make the arrow keys work correctly
    const selectedIndex = ref(-1);

    const onArrowDown = (evt: any) => {
      evt.preventDefault();
      arrowDownHandler(selectedIndex, lineItems.value.length);
    };

    const onArrowUp = (evt: any) => {
      evt.preventDefault();
      arrowUpHandler(selectedIndex);
    };

    const filterOnInput = ({ target }: { target: HTMLInputElement }) => {
      filterOnInputHandler(
        props.useMode === "pickerMode" ? apiTruckLoadLineItems : apiLineItems,
        lineItems,

        target.value,
        filter,
        debouncedFilteredLineItems,

        sortKey,
        sortAsc,
        updateSort,

        selectedIndex,
      );
    };

    const filterOnEnter = (evt: any) => {
      evt.preventDefault();
      let tmpResult = lineItems.value[selectedIndex.value];

      if (!tmpResult) {
        tmpResult = lineItems.value[0];
      }

      if (props.useMode === "pickerMode") {
        sendLineItem(tmpResult);
      } else {
        ctx.root.$router.push(editRouteBuilder(tmpResult.id));
      }
    };

    const sendLineItem = (il: LineItem) => {
      ctx.emit("sendLineItem", il);
    };

    return {
      dataLoaded,
      loadingMessage,
      lineItems,

      filter,
      filterOnEnter,
      filterOnInput,
      clearFilter,

      updateSort,
      localSortIcon,

      editRouteBuilder,

      prettifyLineItemCategory,
      onArrowUp,
      onArrowDown,
      selectedIndex,
      sendLineItem,

      firstInput,
      showMenu,
    };
  },
});
